/**
 * Product Feed Normalization Modal
 *
 * Multi-step wizard for normalizing product feed attribute values
 * Steps: Schema Analysis → Select Fields → Preview Changes → Apply
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import {
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Info,
  Database,
  HelpCircle,
} from 'lucide-react';

interface Column {
  name: string;
  type: string;
  productCount: number;
  uniqueValues: number;
  avgLength: number;
  maxLength: number;
  hasRichInfo: boolean;
  needsNormalization: boolean;
  topValues?: string[];
}

interface SchemaAnalysis {
  totalRecords: number;
  columns: Column[];
  recommendations: {
    enrichmentCandidates: string[];
    normalizationCandidates: string[];
  };
}

interface Normalization {
  field: string;
  before: {
    uniqueValues: number;
    values: string[];
  };
  after: {
    uniqueValues: number;
    values: string[];
  };
  mappings: Record<string, string>;
  reasoning: Record<string, string>;
  affectedProducts: number;
  error?: string;
}

interface NormalizedFieldInfo {
  lastNormalized: string;
  affectedProducts: number;
}

interface ProductFeedNormalizationModalProps {
  open: boolean;
  onClose: () => void;
  dataSourceId: string;
  dataSourceName: string;
}

type Step = 'analysis' | 'select' | 'preview' | 'apply' | 'complete';

export function ProductFeedNormalizationModal({
  open,
  onClose,
  dataSourceId,
  dataSourceName,
}: ProductFeedNormalizationModalProps) {
  const [step, setStep] = useState<Step>('analysis');
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState<SchemaAnalysis | null>(null);
  const [normalizedFields, setNormalizedFields] = useState<Record<string, NormalizedFieldInfo>>({});
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [normalizations, setNormalizations] = useState<Normalization[]>([]);
  const [acceptedFields, setAcceptedFields] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [appliedCount, setAppliedCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    if (open && step === 'analysis') {
      analyzeSchema();
    }
  }, [open, dataSourceId]);

  const analyzeSchema = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/data-sources/${dataSourceId}/analyze-schema`);
      const data = await response.json();

      if (data.success) {
        setSchema(data.analysis);
        setNormalizedFields(data.normalizedFields || {});
        // Pre-select only top 5 normalization candidates (to avoid timeout)
        // Exclude already-normalized fields from auto-selection
        const unnormalizedCandidates = data.analysis.recommendations.normalizationCandidates
          .filter(field => !data.normalizedFields?.[field]);
        const topCandidates = unnormalizedCandidates.slice(0, 5);
        setSelectedFields(topCandidates);
        setStep('select');
      } else {
        throw new Error(data.error || 'Failed to analyze schema');
      }
    } catch (error: any) {
      console.error('Schema analysis failed:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to analyze product feed schema",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const previewNormalizations = async () => {
    if (selectedFields.length === 0) {
      toast({
        title: "No fields selected",
        description: "Please select at least one field to normalize",
        variant: "destructive"
      });
      return;
    }

    if (selectedFields.length > 20) {
      toast({
        title: "Too many fields",
        description: "Please select 20 or fewer fields to avoid timeout. Processing large numbers of fields can take several minutes.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`/data-sources/${dataSourceId}/normalize-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: selectedFields })
      });

      const data = await response.json();

      if (data.success) {
        setNormalizations(data.normalizations);
        setStep('preview');
      } else {
        throw new Error(data.error || 'Failed to preview normalization');
      }
    } catch (error: any) {
      console.error('Preview failed:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to preview normalization",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const applyNormalizations = async () => {
    const fieldsToApply = normalizations.filter(n => acceptedFields.has(n.field));

    if (fieldsToApply.length === 0) {
      toast({
        title: "No fields accepted",
        description: "Please accept at least one field to normalize",
        variant: "destructive"
      });
      return;
    }

    setApplying(true);
    setStep('apply');
    let successCount = 0;

    try {
      for (const norm of fieldsToApply) {
        try {
          const response = await authFetch(`/data-sources/${dataSourceId}/apply-normalization`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              field: norm.field,
              mappings: norm.mappings
            })
          });

          const data = await response.json();

          if (data.success) {
            successCount++;
            setAppliedCount(successCount);
          } else {
            console.error(`Failed to normalize ${norm.field}:`, data.error);
          }
        } catch (error) {
          console.error(`Error normalizing ${norm.field}:`, error);
        }
      }

      setStep('complete');
      toast({
        title: "Normalization Complete",
        description: `Successfully normalized ${successCount} of ${fieldsToApply.length} fields`,
      });

    } catch (error: any) {
      console.error('Normalization failed:', error);
      toast({
        title: "Error",
        description: "Failed to apply normalizations",
        variant: "destructive"
      });
    } finally {
      setApplying(false);
    }
  };

  const toggleField = (fieldName: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const toggleAcceptField = (fieldName: string) => {
    setAcceptedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldName)) {
        newSet.delete(fieldName);
      } else {
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  const handleClose = () => {
    setStep('analysis');
    setSchema(null);
    setSelectedFields([]);
    setNormalizations([]);
    setAcceptedFields(new Set());
    setAppliedCount(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Normalize Product Feed</DialogTitle>
          <DialogDescription>
            {dataSourceName} - Step {step === 'analysis' ? '1' : step === 'select' ? '2' : step === 'preview' ? '3' : step === 'apply' ? '4' : '5'} of 5
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Analysis (Loading) */}
        {step === 'analysis' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">Analyzing product feed schema...</p>
            <p className="text-sm text-muted-foreground mt-2">
              Detecting fields that need normalization
            </p>
          </div>
        )}

        {/* Step 2: Select Fields */}
        {step === 'select' && schema && (
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                <strong>Found {schema.columns.length} attributes</strong> in your product feed.
                Select fields with inconsistent values to normalize.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle>Fields Needing Normalization</CardTitle>
                <CardDescription>
                  {schema.recommendations.normalizationCandidates.length} fields recommended
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {schema.columns
                  .filter(col => schema.recommendations.normalizationCandidates.includes(col.name))
                  .map((col) => (
                    <div key={col.name} className="flex items-start justify-between border rounded-lg p-4">
                      <div className="flex items-start gap-3 flex-1">
                        <Checkbox
                          checked={selectedFields.includes(col.name)}
                          onCheckedChange={() => toggleField(col.name)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{col.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {col.type}
                            </Badge>
                            {normalizedFields[col.name] && (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Normalized
                              </Badge>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm">
                                  <div className="space-y-2">
                                    <div className="font-semibold text-sm border-b pb-1">Field Statistics</div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      <div>
                                        <span className="text-muted-foreground">Type:</span>
                                        <span className="ml-1 font-medium">{col.type}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Products:</span>
                                        <span className="ml-1 font-medium">{col.productCount.toLocaleString()}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Unique values:</span>
                                        <span className="ml-1 font-medium">{col.uniqueValues.toLocaleString()}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Avg length:</span>
                                        <span className="ml-1 font-medium">~{col.avgLength} chars</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Max length:</span>
                                        <span className="ml-1 font-medium">{col.maxLength} chars</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Coverage:</span>
                                        <span className="ml-1 font-medium">
                                          {((col.productCount / (schema?.totalRecords || 1)) * 100).toFixed(1)}%
                                        </span>
                                      </div>
                                    </div>
                                    {col.topValues && col.topValues.length > 0 && (
                                      <div className="border-t pt-2 mt-2">
                                        <div className="font-semibold text-xs mb-1">Sample Values (top 5):</div>
                                        <div className="space-y-0.5">
                                          {col.topValues.slice(0, 5).map((value, idx) => (
                                            <div key={idx} className="text-xs text-muted-foreground truncate">
                                              • {value}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {normalizedFields[col.name] && (
                                      <div className="border-t pt-2 mt-2">
                                        <div className="text-xs text-green-600">
                                          <div><strong>Last normalized:</strong> {new Date(normalizedFields[col.name].lastNormalized).toLocaleString()}</div>
                                          <div><strong>Products affected:</strong> {normalizedFields[col.name].affectedProducts.toLocaleString()}</div>
                                        </div>
                                      </div>
                                    )}
                                    <div className="border-t pt-2 text-xs text-muted-foreground">
                                      {col.needsNormalization ?
                                        '✓ Recommended for normalization (inconsistent values detected)' :
                                        col.hasRichInfo ?
                                        '✓ Candidate for text extraction (contains rich information)' :
                                        'Clean field - no action needed'
                                      }
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground">
                            <span>{col.uniqueValues} unique values</span>
                            <span>{col.productCount} products</span>
                            <span>~{col.avgLength} chars</span>
                            {normalizedFields[col.name] && (
                              <span className="text-green-600">
                                Last: {new Date(normalizedFields[col.name].lastNormalized).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                {schema.recommendations.normalizationCandidates.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No fields need normalization. Your data is already clean!
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={previewNormalizations}
                disabled={selectedFields.length === 0 || loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Preview Normalization
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview Changes */}
        {step === 'preview' && (
          <div className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <Sparkles className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <strong>AI Analysis Complete!</strong> Review the proposed normalizations below.
                Accept or reject each field individually.
              </AlertDescription>
            </Alert>

            {/* Show errors if any fields failed */}
            {normalizations.some((norm) => norm.error) && (
              <Alert variant="destructive">
                <AlertDescription>
                  <strong>Some fields could not be analyzed:</strong>
                  <ul className="list-disc list-inside mt-2">
                    {normalizations
                      .filter((norm) => norm.error)
                      .map((norm) => (
                        <li key={norm.field}>
                          <strong>{norm.field}</strong>: {norm.error}
                        </li>
                      ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {normalizations
                .filter((norm) => !norm.error) // Skip fields with errors
                .map((norm) => (
                <Card key={norm.field} className={acceptedFields.has(norm.field) ? 'border-green-500' : ''}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{norm.field}</CardTitle>
                        <CardDescription>
                          {norm.affectedProducts} products will be updated
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="bg-blue-50">
                        {norm.before.uniqueValues} → {norm.after.uniqueValues} values
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Before/After Comparison */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="font-medium mb-2">Before (duplicates)</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {Object.keys(norm.mappings).map((oldValue) => (
                            <div key={oldValue} className="text-muted-foreground line-through">
                              {oldValue}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">After (normalized)</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {norm.after.values.slice(0, 10).map((value) => (
                            <div key={value} className="text-green-600 font-medium">
                              {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* AI Reasoning */}
                    {Object.keys(norm.reasoning).length > 0 && (
                      <Alert className="bg-blue-50 border-blue-200">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-900">
                          <strong>AI Analysis:</strong>
                          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                            {Object.entries(norm.reasoning).slice(0, 3).map(([value, reason]) => (
                              <li key={value}>
                                <strong>{value}:</strong> {reason}
                              </li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Accept/Reject buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant={acceptedFields.has(norm.field) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleAcceptField(norm.field)}
                        className="flex-1"
                      >
                        {acceptedFields.has(norm.field) ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Accepted
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Accept
                          </>
                        )}
                      </Button>
                      {acceptedFields.has(norm.field) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleAcceptField(norm.field)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep('select')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={applyNormalizations}
                disabled={acceptedFields.size === 0}
              >
                Apply {acceptedFields.size} Normalization{acceptedFields.size !== 1 ? 's' : ''}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Applying */}
        {step === 'apply' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">Applying normalizations...</p>
            <p className="text-sm text-muted-foreground mt-2">
              Updated {appliedCount} of {acceptedFields.size} fields
            </p>
          </div>
        )}

        {/* Step 5: Complete */}
        {step === 'complete' && (
          <div className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <strong>Normalization Complete!</strong> Your product feed has been cleaned.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fields normalized:</span>
                  <span className="font-medium">{appliedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total products affected:</span>
                  <span className="font-medium">
                    {normalizations
                      .filter(n => acceptedFields.has(n.field))
                      .reduce((sum, n) => sum + n.affectedProducts, 0)
                      .toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Alert>
              <Database className="h-4 w-4" />
              <AlertDescription>
                <strong>Next Steps:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Product feed data has been updated</li>
                  <li>Search queries will now match normalized values</li>
                  <li>Embeddings will be consistent across variants</li>
                  <li>Q&A topics can be regenerated with clean data</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="flex justify-end pt-4">
              <Button onClick={handleClose}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
